import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Users, Key, Shield } from "lucide-react";
import { WorkspaceTab } from "@/components/Settings/WorkspaceTab";
import { TeamMembersTab } from "@/components/Settings/TeamMembersTab";
import { ApiKeysTab } from "@/components/Settings/ApiKeysTab";
import { AdminBillingPanel } from "@/components/Billing/AdminBillingPanel";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export default function Settings() {
  const { user } = useAuth();
  
  // Check if user is admin (admin@verbeo.ai or has is_admin metadata)
  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin", user?.id, user?.email],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase.auth.getUser();
      if (!data.user) return false;
      
      // Check if email is admin@verbeo.ai
      if (data.user.email === 'admin@verbeo.ai') {
        return true;
      }
      
      // Check if user has is_admin metadata
      return (data.user.user_metadata?.is_admin as boolean) || false;
    },
    enabled: !!user,
  });

  return (
    <div className="h-full p-8 overflow-auto">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage your workspaces, team members, and API keys
          </p>
        </div>

        <Tabs defaultValue="workspaces" className="w-full">
          <TabsList className={isAdmin ? "grid w-full max-w-lg grid-cols-4" : "grid w-full max-w-md grid-cols-3"}>
            <TabsTrigger value="workspaces" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Workspaces
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Team
            </TabsTrigger>
            <TabsTrigger value="api-keys" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              API Keys
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="admin-billing" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Admin Billing
              </TabsTrigger>
            )}
          </TabsList>

          <div className="mt-6">
            <TabsContent value="workspaces" className="m-0">
              <WorkspaceTab />
            </TabsContent>

            <TabsContent value="team" className="m-0">
              <TeamMembersTab />
            </TabsContent>

            <TabsContent value="api-keys" className="m-0">
              <ApiKeysTab />
            </TabsContent>
            {isAdmin && (
              <TabsContent value="admin-billing" className="m-0">
                <AdminBillingPanel />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
}
